import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogProps } from '@economic/taco';
import { useCurrentUser } from '@economic/react-app';
import { t, useLocalization } from '../../../../../../../shared/components/l10n/l10n';

import SignupDialogContent from './SignupModalContent';
import { Journal, OnboardingStatus } from '../../../../../../../../types/internal-api';
import useQuery from '../../../../../../../shared/hooks/useQuery';
import internalApiClient from '../../../../../../../shared/util/api/internalApiClient';
import { SMARTPAY_SIGNUP_STEPS, smartPayValidAccountTypes } from '../../../types';

import AddJournalDrawer from './PaymentDataStep/AddJournalDrawer';
import { sanitizeCVRNumber } from './utils';

interface Props extends Partial<DialogProps> {
    refetchState?: () => void;
    inMarket?: boolean;
}

const SignupDialog = ({ refetchState, inMarket, ...dialogProps }: Props) => {
    const signupSteps = Object.keys(SMARTPAY_SIGNUP_STEPS);

    const loadingTexts = useLocalization(textIds);
    const navigate = useNavigate();
    const location = useLocation();
    const user = useCurrentUser();

    const { data: onboardingData, loading: onboardingLoading } = useQuery<OnboardingStatus>('/onboarding');
    const { data: journals, loading: journalsLoading, refetch: refetchJournals } = useQuery<Journal[]>('/journals');
    const { data: paymentAccounts, loading: paymentAccountsLoading } = useQuery('/paymentAccounts/accounts');
    const { data: feeAccounts, loading: feeAccountsLoading } = useQuery('/accounts');

    const filteredFeeAccounts = feeAccounts?.filter(fa => smartPayValidAccountTypes.includes(fa.type.number));

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [checkCustomerData, setCheckCustomerData] = useState<any>(null);
    const [preSelectedJournalName, setPreSelectedJournalName] = useState<string>();

    useEffect(() => {
        if (!onboardingData) return;
        checkCustomer();
    }, [onboardingData]);

    const checkCustomer = () => {
        if (!onboardingData.companyData.cvr) {
            setCheckCustomerData(undefined);
            return;
        }

        internalApiClient
            .get(`/smartpay/checkCustomer?cvr=${sanitizeCVRNumber(onboardingData.companyData.cvr)}`)
            .then(response => setCheckCustomerData(response.data));
    };

    const onDialogClose = (hide?: () => void) => {
        if (signupSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.INFORMATION_SENT) {
            refetchState && refetchState();
        }

        if (typeof hide === 'function') hide();

        setCurrentStepIndex(0);

        if (inMarket) {
            // remove modal hash
            navigate(location.pathname);

            if (signupSteps[currentStepIndex] !== SMARTPAY_SIGNUP_STEPS.INFORMATION_SENT) return;

            // refresh page and close the modal after completing the onboarding
            if (window.location.pathname.includes('addon/smartpay')) {
                window.location.assign(location.pathname);
            } else {
                navigate('/market/addon/smartpay');
            }
        }
    };

    const isDataLoading =
        onboardingLoading || journalsLoading || paymentAccountsLoading || feeAccountsLoading || checkCustomerData === null;

    return (
        <Dialog size="xs" onClose={() => onDialogClose()} {...dialogProps}>
            <Dialog.Content aria-label={t`SMARTPAY_ONBOARDING_MODAL_ACCESSIBILITY_NAME`}>
                {({ close, drawer }) => (
                    <SignupDialogContent
                        currentFlowSteps={signupSteps}
                        loadingTexts={loadingTexts}
                        isDataLoading={isDataLoading}
                        onDialogClose={() => onDialogClose(close)}
                        currentStepIndex={currentStepIndex}
                        setCurrentStepIndex={setCurrentStepIndex}
                        onboardingData={onboardingData}
                        userData={user}
                        preSelectedJournalName={preSelectedJournalName}
                        journals={journals}
                        paymentAccounts={paymentAccounts}
                        feeAccounts={filteredFeeAccounts}
                        refetchState={refetchState}
                        inMarket={inMarket}
                        novemberFirstCustomerData={checkCustomerData}
                        drawerProps={drawer}
                    />
                )}
            </Dialog.Content>
            <Dialog.Drawer>
                {drawerProps => (
                    <AddJournalDrawer
                        drawerProps={drawerProps}
                        refetchJournals={refetchJournals}
                        setPreSelectedJournalName={setPreSelectedJournalName}
                    />
                )}
            </Dialog.Drawer>
        </Dialog>
    );
};

export default SignupDialog;

// load translations in the modal because this modal is used in market too
const textIds = [
    'SMARTPAY_ONBOARDING_MODAL_ACCESSIBILITY_NAME',
    'SMARTPAY_ONBOARDING_MODAL_DELIVERED_BY',
    'SMARTPAY_ONBOARDING_MODAL_BACK_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_NEXT_BUTTON',

    'SMARTPAY_ONBOARDING_MODAL_CONSENT_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_CONSENT_PARAGRAPH_1',
    'SMARTPAY_ONBOARDING_MODAL_CONSENT_PARAGRAPH_2',
    'SMARTPAY_ONBOARDING_MODAL_CONSENT_ONBOARDING_ALREADY_IN_PROGRESS',
    'SMARTPAY_ONBOARDING_MODAL_CONSENT_ONBOARDING_CVR_ALREADY_ONBOARDED',
    'SMARTPAY_ONBOARDING_MODAL_CONSENT_CLOSE_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_CONSENT_ACCEPT_BUTTON',

    'SMARTPAY_ONBOARDING_MODAL_SELECT_PLAN_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_SELECT_PLAN_TEXT',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_NAME',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_PRICE',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_STANDARD_FEATURES',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_NAME',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_PRICE',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_FEATURES',
    'SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_DISCLAIMER',

    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_TEXT',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR_REQUIRED_VALIDATION_ERROR',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR_VALIDATION_ERROR',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR_INEXISTENT_ERROR',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_NAME',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_EMAIL',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_PHONE',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_PHONE_VALIDATION_ERROR',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_MASTER_INFO_BUTTON',

    'SMARTPAY_ONBOARDING_MODAL_USER_DATA_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_USER_DATA_TEXT',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_FIRST_NAME',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_LAST_NAME',
    'SMARTPAY_ONBOARDING_MODAL_NEWSLETTER_CONSENT',

    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_DESCRIPTION_TEXT_EXISTING_WITHOUT_INTEGRATION',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_DESCRIPTION_TEXT_EXISTING_WITH_INTEGRATION',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_DESCRIPTION_TEXT_NEW_CUSTOMER',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_EXISTING_CUSTOMER_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_CUSTOMER_NUMBER_DROPDOWN_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CUSTOMER_NUMBER_VALIDATION_ERROR',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_BANK_ACCOUNT_SETTINGS_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_BANK_ACCOUNT_DROPDOWN_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_JOURNAL_SETTINGS_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_JOURNAL_DROPDOWN_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_FEE_ACCOUNT_DESCRIPTION_TEXT',
    'SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_FEE_ACCOUNT_DROPDOWN_TITLE',

    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_TEXT',
    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_JOURNAL_NAME_INPUT_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_APPROVAL_REQUIRED_INPUT_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_LOCK_VAT_INPUT_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_CANCEL_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_SAVE_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_ADD_JOURNAL_ERROR',

    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_PARAGRAPH_1',
    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_NEXT_STEPS_1',
    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_NEXT_STEPS_2',
    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_NEXT_STEPS_3',
    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_PARAGRAPH_2',
    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_CONFIRM_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_SUCCESS_REDIRECT_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_EXISTING_CUSTOMER_SUCCESS_TITLE',
    'SMARTPAY_ONBOARDING_MODAL_EXISTING_CUSTOMER_SUCCESS_CONTENT',
    'SMARTPAY_ONBOARDING_MODAL_EXISTING_CUSTOMER_SUCCESS_CONTENT_SMARTPAY_BUTTON',
    'SMARTPAY_ONBOARDING_MODAL_EXISTING_CUSTOMER_CVR_ONBOARDED_ERROR',
];
